import { useCallback, useEffect, useMemo, useState } from "react";
import { useApolloClient } from "@apollo/client";
import { Curriculum, DataQuery, PageInfo } from "@models/ISchema";
import { curriculumQuery } from "../graphql/curriculum.queries";

interface UseGetCurriculumOptionsParams {
  scenario: string;
  campusId?: string;
  departmentId?: string;
  modalityId?: string;
  shiftId?: string;
  programId?: string;
  activeCode?: boolean;
  ignoreAccessFilters?: boolean;
  /**
   * Key to reset the search term and page
   */
  key?: string;
}

const useGetCurriculumOptions = ({
  scenario,
  activeCode,
  campusId,
  departmentId,
  modalityId,
  shiftId,
  programId,
  ignoreAccessFilters,
  key,
}: UseGetCurriculumOptionsParams) => {
  const client = useApolloClient();
  const [localSearchTerm, setLocalSearchTerm] = useState("");
  const [curriculumPage, setCurriculumPage] = useState(1);

  useEffect(() => {
    setLocalSearchTerm("");
    setCurriculumPage(1);
  }, [key]);

  const variablesCurriculum = useMemo(() => {
    const hasSomeFilter = !!campusId || !!departmentId || !!modalityId || !!shiftId || !!programId;

    const filters = Object.values({
      campusId: campusId ? { campusId: { eq: campusId } } : undefined,
      departmentId: departmentId ? { departmentId: { eq: departmentId } } : undefined,
      modalityId: modalityId ? { modalityId: { eq: modalityId } } : undefined,
      shiftId: shiftId ? { shiftId: { eq: shiftId } } : undefined,
      code: programId ? { code: { eq: programId } } : undefined,
    }).filter(filter => !!filter);

    return hasSomeFilter || !!programId
      ? {
          fields: {
            and: [
              {
                program: hasSomeFilter
                  ? {
                      ...Object.assign({}, ...filters),
                    }
                  : undefined,
              },
            ],
          },
        }
      : {};
  }, [scenario]);

  const getCurriculums = useCallback(
    async (searchTerm = "", page = 1) => {
      const optionsPerPage = 10;

      try {
        const variables = {
          query: curriculumQuery,
          variables: {
            scenarioId: scenario,
            filter: {
              ...variablesCurriculum,
              groupByCode: true,
              ...(ignoreAccessFilters !== undefined && { ignoreAccessFilters }),
              pagination: {
                page,
                size: optionsPerPage,
                searchTerm,
              },
            },
          },
        };

        const { data } = await client.query(variables);
        const dataQuery: DataQuery = data?.data;
        const pageInfo: PageInfo = dataQuery?.curriculums?.pageInfo;

        const options = [];

        if (dataQuery.curriculums.items.length > 0) {
          dataQuery.curriculums.items.forEach((curriculum: Curriculum) => {
            options.push({
              label: curriculum?.code,
              value: activeCode ? curriculum.code : curriculum.id,
              self: curriculum,
            });
          });
        }

        return { pageInfo, options };
      } catch (error) {
        console.error(error);
        return null;
      }
    },
    [client, ignoreAccessFilters],
  );

  const loadCurriculumOptions = async (searchTerm: string) => {
    let searchPage = 1;

    if (localSearchTerm === searchTerm) {
      searchPage = curriculumPage;
    } else {
      setLocalSearchTerm(searchTerm);
    }
    const { pageInfo, options: curriculumOptions } = await getCurriculums(searchTerm, searchPage);
    setCurriculumPage(curriculumPage + 1);
    return {
      options: curriculumOptions,
      hasMore: pageInfo?.hasNextPage,
      additional: {
        page: curriculumPage,
      },
    };
  };

  return {
    loadCurriculumOptions,
  };
};

export default useGetCurriculumOptions;
